import { useEffect, useRef, useState } from 'react'
import PregateHeader from '../components/PregateHeader'

/**
 * Public landing page at `/`. Must render fully with zero backend dependency —
 * see docs/decisions/2026-08-04-pow-returning-flag.md. Only imports
 * PregateHeader from src/components/ — small and dependency-free by design,
 * same precedent as ErrorFallback.tsx, so it carries none of the risk a
 * heavier components/ import would add to a zero-backend render.
 *
 * Desktop/mobile switch at a single breakpoint (Tailwind arbitrary variant
 * min-[768px]:), per the approved design in design/previews/landing_v2/. The design
 * itself specifies 720px, but the pipeline's fixed card/connector widths (calc-based,
 * min-width 190px per stage) don't actually fit until ~760px — confirmed by measuring
 * horizontal overflow across 720-810px. Raised to Tailwind's standard 768px (md) rather
 * than shrinking the approved card widths, so row-mode only ever renders where it fits.
 */

const BACKEND = import.meta.env.VITE_API_URL ?? ''
const INVITE_EMAIL = 'invites@getproofofwork.com'
// ASCII hyphen, not an em dash — non-ASCII characters in mailto: subjects are handled
// inconsistently across mail clients and fail silently.
const INVITE_MAILTO_HREF = `mailto:${INVITE_EMAIL}?subject=${encodeURIComponent(
  'Invite request - Proof of Work',
)}&body=${encodeURIComponent(
  "Hi Yosef,\n\nI'd like to request an invitation to Proof of Work.\n\nName / company:\n\nWhat I'd use it for:\n\nThanks!\n",
)}`

const FOCUS_RING =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent'

const PIPELINE_STAGES = [
  {
    number: '01',
    title: 'Collect',
    body: 'Record accomplishments as they happen — or start from the old CVs, half-written notes and project descriptions you already have.',
  },
  {
    number: '02',
    title: 'Structure',
    body: 'accomplishments are turned into structured proofs: what you did, its context, and your impact',
  },
  {
    number: '03',
    title: 'Match',
    body: 'Say what the CV is for. Proof of Work finds the experience in your library that supports it.',
  },
  {
    number: '04',
    title: 'Assemble',
    body: 'It writes the document.',
  },
] as const

const OUTPUTS = [
  { label: 'Tailored CV', planned: false },
  { label: 'Quarterly review', planned: true },
  { label: 'Promotion case', planned: true },
] as const

export default function LandingPage() {
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const inviteSectionRef = useRef<HTMLElement>(null)
  const copyTimeoutRef = useRef<number | null>(null)

  // Non-blocking backend warm-up. Never gates rendering; failure is silent.
  useEffect(() => {
    fetch(`${BACKEND}/health`).catch(() => {})
  }, [])

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current)
    }
  }, [])

  const handleScrollToInvite = () => {
    const el = inviteSectionRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      top: rect.top + window.scrollY - 32,
      behavior: reduceMotion ? 'auto' : 'smooth',
    })
  }

  const handleCopyAddress = () => {
    if (!navigator.clipboard?.writeText) {
      setCopyFailed(true)
      setCopied(false)
      return
    }
    navigator.clipboard
      .writeText(INVITE_EMAIL)
      .then(() => {
        setCopyFailed(false)
        setCopied(true)
        if (copyTimeoutRef.current) window.clearTimeout(copyTimeoutRef.current)
        copyTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000)
      })
      .catch(() => {
        setCopyFailed(true)
        setCopied(false)
      })
  }

  const copyAnnouncement = copied
    ? 'Copied to clipboard.'
    : copyFailed
      ? "Couldn't copy automatically — select the address above to copy it manually."
      : ''

  return (
    <div className="min-h-screen w-full bg-brand-surface text-brand-ink font-body antialiased px-[22px] pt-[18px] min-[768px]:px-[44px] min-[768px]:pt-[28px]">
      <div className="mx-auto w-full min-[768px]:max-w-[960px]">
        <PregateHeader />

        <main>
        <section className="pt-[34px] pb-[40px] min-[768px]:pt-[60px] min-[768px]:pb-[58px]">
          <h1 className="max-w-full min-[768px]:max-w-[720px] font-serif font-medium text-[33px] min-[768px]:text-[52px] leading-[1.1] tracking-[-0.02em] mb-[16px] min-[768px]:mb-[20px] text-pretty">
            Your work is bigger than your CV.
          </h1>
          <p className="max-w-full min-[768px]:max-w-[720px] text-[16.5px] min-[768px]:text-[18.5px] leading-[1.55] text-brand-slate mb-[14px] min-[768px]:mb-[18px] text-pretty">
            Your best work gets compressed into a few bullet points. The numbers, decisions
            and context behind it disappear.
          </p>
          <p className="max-w-full min-[768px]:max-w-[720px] text-[16.5px] min-[768px]:text-[18.5px] leading-[1.55] text-brand-slate mb-[22px] min-[768px]:mb-[28px] text-pretty">
            Proof of Work keeps your professional accomplishments in one structured library,
            so you can build the CV you need.
          </p>
          <div className="flex flex-col items-start gap-[12px]">
            <button
              type="button"
              onClick={handleScrollToInvite}
              className={`rounded-[3px] bg-brand-accent px-[22px] py-[13px] text-[15px] font-medium tracking-[0.01em] text-brand-surface transition-colors hover:bg-[#6B3109] ${FOCUS_RING}`}
            >
              Request an invitation
            </button>
            <span className="text-[13px] text-brand-slate">
              Invite-only while the product is in early access.
            </span>
          </div>
        </section>

        <section className="border-t border-brand-ink/[0.12] pt-[34px] pb-[40px] min-[768px]:pt-[52px] min-[768px]:pb-[58px]">
          <h2 className="max-w-full min-[768px]:max-w-[720px] font-serif font-medium text-[25px] min-[768px]:text-[32px] leading-[1.2] tracking-[-0.015em] mb-[22px] min-[768px]:mb-[28px]">
            Build your record once. Use it whenever.
          </h2>

          <PipelineDiagram />

          <p className="max-w-full min-[768px]:max-w-[720px] text-[14px] leading-[1.6] text-brand-slate mt-[20px] min-[768px]:mt-[26px] text-pretty">
            Your experience doesn&apos;t change. The story you need to tell does.
          </p>
        </section>

        <section
          ref={inviteSectionRef}
          className="border-t border-brand-ink/[0.12] pt-[34px] pb-[40px] min-[768px]:pt-[52px] min-[768px]:pb-[58px]"
        >
          <h2 className="font-serif font-medium text-[25px] min-[768px]:text-[32px] leading-[1.2] tracking-[-0.015em] mb-[14px] min-[768px]:mb-[18px]">
            Early access is invite-only.
          </h2>
          <p className="max-w-full min-[768px]:max-w-[720px] text-[15.5px] min-[768px]:text-[17px] leading-[1.65] text-brand-slate mb-[14px] min-[768px]:mb-[18px] text-pretty">
            I&apos;m opening Proof of Work to a small number of people while the core product
            is still taking shape.
          </p>
          <p className="max-w-full min-[768px]:max-w-[720px] text-[15.5px] min-[768px]:text-[17px] leading-[1.65] text-brand-slate mb-[24px] min-[768px]:mb-[26px] text-pretty">
            If the problem is one you recognize, ask for an invitation and I&apos;ll get back
            to you.
          </p>

          <div className="flex flex-col items-start gap-[16px]">
            <a
              href={INVITE_MAILTO_HREF}
              className={`rounded-[3px] bg-brand-accent px-[22px] py-[13px] text-[15px] font-medium tracking-[0.01em] text-brand-surface transition-colors hover:bg-[#6B3109] ${FOCUS_RING}`}
            >
              Request an invitation
            </a>
            <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[8px] text-[13.5px] text-brand-slate">
              <span>
                Or email <span className="font-medium text-brand-ink">{INVITE_EMAIL}</span>
              </span>
              <button
                type="button"
                onClick={handleCopyAddress}
                className={`w-[112px] flex-none rounded-[3px] border bg-white py-[7px] text-center text-[12.5px] font-medium transition-colors hover:border-brand-ink ${FOCUS_RING} ${
                  copied ? 'border-brand-accent text-brand-accent' : 'border-brand-ink/35 text-brand-slate'
                }`}
              >
                {copied ? 'Copied' : 'Copy address'}
              </button>
              <span className="sr-only" role="status" aria-live="polite">
                {copyAnnouncement}
              </span>
            </div>
          </div>
        </section>
        </main>

        <footer className="flex flex-wrap justify-between gap-x-7 gap-y-2.5 border-t border-brand-ink/[0.12] pt-[20px] pb-[34px] text-[12.5px] text-brand-slate">
          <div className="flex flex-wrap items-baseline gap-x-7 gap-y-2.5">
            <span>
              Built by{' '}
              <a
                href="https://linkedin.com/in/sukenik"
                rel="noopener noreferrer"
                className={`text-brand-accent underline-offset-2 hover:text-brand-ink hover:underline ${FOCUS_RING}`}
              >
                Yosef Sukenik
              </a>
            </span>
            <a
              href="/why"
              className={`inline-block py-[4px] text-brand-accent underline-offset-2 hover:text-brand-ink hover:underline ${FOCUS_RING}`}
            >
              Why I built this →
            </a>
          </div>
          <span className="flex w-full gap-[10px] min-[600px]:w-auto">
            <a href="/privacy" className={`hover:text-brand-ink hover:underline ${FOCUS_RING}`}>
              Privacy
            </a>
            <span className="text-brand-slate/85">·</span>
            <a href="/terms" className={`hover:text-brand-ink hover:underline ${FOCUS_RING}`}>
              Terms
            </a>
          </span>
        </footer>
      </div>
    </div>
  )
}

function PipelineDiagram() {
  return (
    <>
      <div className="hidden min-[768px]:block">
        <PipelineRow />
      </div>
      <div className="min-[768px]:hidden">
        <PipelineVertical />
      </div>
    </>
  )
}

function PipelineRow() {
  return (
    <div>
      <div className="flex items-stretch">
        <StageCard {...PIPELINE_STAGES[0]} />
        <RowConnector />
        <StageCard {...PIPELINE_STAGES[1]} />
        <RowConnector />
        <LibraryCard />
      </div>

      <div className="py-[12px]" aria-hidden="true">
        <div className="ml-[calc(100%_-_136px)] h-[26px] w-[2px] bg-brand-accent" />
        <div className="ml-[calc((100%_-_334px)/4)] h-[2px] w-[calc(100%_-_134px_-_(100%_-_334px)/4)] bg-brand-accent" />
        <div className="relative ml-[calc((100%_-_334px)/4)] h-[26px] w-[2px] bg-brand-accent">
          <span className="absolute -left-[3px] bottom-0 h-[8px] w-[8px] rotate-45 border-b-2 border-r-2 border-brand-accent" />
        </div>
      </div>

      <div className="flex items-stretch">
        <StageCard {...PIPELINE_STAGES[2]} />
        <RowConnector />
        <StageCard {...PIPELINE_STAGES[3]} />
        <div className="w-[72px] flex-none self-center" aria-hidden="true">
          <BranchConnectorRow />
        </div>
        <div className="flex w-[230px] flex-none flex-col gap-[14px] self-center">
          {OUTPUTS.map((output) => (
            <OutputBox key={output.label} {...output} />
          ))}
        </div>
      </div>
    </div>
  )
}

function PipelineVertical() {
  return (
    <div>
      <StageCardVertical {...PIPELINE_STAGES[0]} />
      <ConnectorVertical />
      <StageCardVertical {...PIPELINE_STAGES[1]} />
      <ConnectorVertical />
      <LibraryCardVertical />
      <ConnectorVertical />
      <StageCardVertical {...PIPELINE_STAGES[2]} />
      <ConnectorVertical />
      <StageCardVertical {...PIPELINE_STAGES[3]} />
      <div className="ml-[26px] flex h-[22px]" aria-hidden="true">
        <span className="w-[2px] bg-brand-accent" />
      </div>
      <div className="ml-[26px] flex items-start">
        <div className="flex-none" aria-hidden="true">
          <BranchConnectorVertical />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-[14px]">
          {OUTPUTS.map((output) => (
            <OutputBox key={output.label} {...output} />
          ))}
        </div>
      </div>
    </div>
  )
}

interface Stage {
  number: string
  title: string
  body: string
}

function StageCard({ number, title, body }: Stage) {
  return (
    <div className="flex w-[calc((100%_-_334px)/2)] min-w-[190px] flex-none flex-col rounded-[3px] border border-brand-ink/[0.16] bg-white px-[20px] pb-[20px] pt-[18px]">
      <div className="mb-[10px] text-[11px] tracking-[0.16em] text-brand-slate">{number}</div>
      <div className="mb-[9px] font-serif text-[23px] font-medium tracking-[-0.01em]">{title}</div>
      <div className="text-[13.5px] leading-[1.6] text-brand-slate text-pretty">{body}</div>
    </div>
  )
}

function StageCardVertical({ number, title, body }: Stage) {
  return (
    <div className="rounded-[3px] border border-brand-ink/[0.16] bg-white px-[18px] pb-[18px] pt-[16px]">
      <div className="mb-[8px] flex items-baseline gap-[12px]">
        <span className="text-[11px] tracking-[0.16em] text-brand-slate">{number}</span>
        <span className="font-serif text-[22px] font-medium tracking-[-0.01em]">{title}</span>
      </div>
      <div className="text-[14px] leading-[1.6] text-brand-slate text-pretty">{body}</div>
    </div>
  )
}

function LibraryCard() {
  return (
    <div className="flex w-[270px] flex-none flex-col justify-center gap-[7px] rounded-[3px] bg-brand-ink px-[22px] py-[20px] text-brand-surface">
      <span className="font-serif text-[30px] font-medium leading-[1.05] tracking-[-0.015em]">Library</span>
      <span className="text-[14px] leading-[1.5] text-brand-surface/80">Your professional record</span>
    </div>
  )
}

function LibraryCardVertical() {
  return (
    <div className="rounded-[3px] bg-brand-ink px-[20px] pb-[22px] pt-[20px] text-brand-surface">
      <div className="mb-[6px] font-serif text-[28px] font-medium tracking-[-0.015em]">Library</div>
      <div className="text-[14.5px] text-brand-surface/80">Your professional record</div>
    </div>
  )
}

function RowConnector() {
  return (
    <div className="flex w-[32px] flex-none items-center self-center px-[5px]" aria-hidden="true">
      <span className="relative block h-[2px] w-full bg-brand-accent">
        <span className="absolute -top-[3px] right-0 h-[8px] w-[8px] rotate-45 border-r-2 border-t-2 border-brand-accent" />
      </span>
    </div>
  )
}

function ConnectorVertical() {
  return (
    <div className="flex h-[34px] justify-center py-[6px]" aria-hidden="true">
      <span className="relative w-[2px] bg-brand-accent">
        <span className="absolute -left-[3px] bottom-0 h-[8px] w-[8px] rotate-45 border-b-2 border-r-2 border-brand-accent" />
      </span>
    </div>
  )
}

function BranchConnectorRow() {
  return (
    <svg width="72" height="166" viewBox="0 0 72 166" fill="none">
      <path d="M8 83 H24" stroke="#833C0B" strokeWidth="2" />
      <path d="M24 83 V23 H58" stroke="#833C0B" strokeWidth="2" />
      <path d="M24 83 H58" stroke="rgba(31,42,55,0.42)" strokeWidth="2" strokeDasharray="7 5" />
      <path d="M24 83 V143 H58" stroke="rgba(31,42,55,0.42)" strokeWidth="2" strokeDasharray="7 5" />
      <path d="M58 17 L68 23 L58 29" stroke="#833C0B" strokeWidth="2" />
      <path d="M58 77 L68 83 L58 89" stroke="rgba(31,42,55,0.5)" strokeWidth="2" />
      <path d="M58 137 L68 143 L58 149" stroke="rgba(31,42,55,0.5)" strokeWidth="2" />
    </svg>
  )
}

function BranchConnectorVertical() {
  return (
    <svg width="46" height="184" viewBox="0 0 46 184" fill="none">
      <path d="M1 0 V26 H32" stroke="#833C0B" strokeWidth="2" />
      <path d="M1 26 V158" stroke="rgba(31,42,55,0.42)" strokeWidth="2" strokeDasharray="7 5" />
      <path d="M1 92 H32" stroke="rgba(31,42,55,0.42)" strokeWidth="2" strokeDasharray="7 5" />
      <path d="M1 158 H32" stroke="rgba(31,42,55,0.42)" strokeWidth="2" strokeDasharray="7 5" />
      <path d="M32 20 L42 26 L32 32" stroke="#833C0B" strokeWidth="2" />
      <path d="M32 86 L42 92 L32 98" stroke="rgba(31,42,55,0.5)" strokeWidth="2" />
      <path d="M32 152 L42 158 L32 164" stroke="rgba(31,42,55,0.5)" strokeWidth="2" />
    </svg>
  )
}

interface Output {
  label: string
  planned: boolean
}

function OutputBox({ label, planned }: Output) {
  if (!planned) {
    return (
      <div className="flex h-[52px] min-[768px]:h-[46px] items-center rounded-[3px] border-2 border-brand-ink bg-white px-[13px] text-[14.5px] font-semibold text-brand-ink">
        {label}
      </div>
    )
  }

  return (
    <div className="flex h-[52px] min-[768px]:h-[46px] items-center justify-between gap-[10px] rounded-[3px] border-2 border-dashed border-brand-ink/[0.32] px-[11px]">
      <span className="whitespace-nowrap text-[14.5px] text-brand-slate">{label}</span>
      <span className="whitespace-nowrap rounded-[2px] border border-dashed border-brand-ink/45 px-[8px] py-[4px] text-[12px] font-medium uppercase tracking-[0.08em] text-brand-slate">
        Planned
      </span>
    </div>
  )
}
